import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { useMemo } from 'react'
import { formatPlanDateRange } from '../../../../../../../utils/utils'
import type { ConnectedSummaryLine } from '../../../../../components/ConnectedSummaryLines'
import { ConnectedSummaryLinesV2 } from '../../../../../components/ConnectedSummaryLinesV2'
import {
  isListPlansFetchFailed,
  isListPlansHeaderLoading,
  useLumpsumPlansContext
} from '../../LumpsumPlansProvider'
import { useBottomSheet } from '../../../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../../../utils/appBridge'
import type { LumpsumRebalancePlansResponse } from '../../../api/rebalance.types'
import { PlansLumpsumListEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import { MissedGainsInfoBottomSheet } from './MissedGainsInfoBottomSheet'
import { RebalancePlanInfoBottomSheet } from './RebalancePlanInfoBottomSheet'
import { RebalancePlanUpdatedBottomSheet } from './RebalancePlanUpdatedBottomSheet'
import {
  ListPlansPlanUpdatedBanner,
  ListPlansPlanUpdatedBannerType,
  shouldShowPlanUpdatedBanner
} from '../common/ListPlansPlanUpdatedBanner'
import { HeaderInfoAfterWord } from '../common/ListPlansHeaderShared'
import { HeaderHeroDescription } from '../../lumpsum-ui-components/HeaderHeroDescription'
import { HeaderHeroSkeleton } from '../../lumpsum-ui-components/HeaderHeroSkeleton'
import { HeaderRiveBackdrop } from '../common/HeaderRiveBackdrop'
import {
  getPlansFlowTheme,
  PlansThemeFlow,
  resolveRebalanceHeaderAssetAppearance,
  type LumpsumHeaderAppearance
} from '../common/riskProfileHeaderTheme'
import type { AllocationRiskProfile } from '../../../../../expert-review/types/allocation.types'
import { PlansPlanMode } from '../../../api/types'
import { isPlansListFullyEmpty } from '../../../../utils/lumpsum/planListUtils'

const REBALANCE_PLAN_DESCRIPTION =
  'This plan optimises portfolio performance in a tax-efficient manner'
const REBALANCE_TOP_SHAPE_STATUS_COPY =
  'No changes are needed this month. We’ll review it again next month to make sure it stays that way.'

export function LumpsumRebalanceHeader() {
  const { planMode, rebalancePlansData, rebalancePlansQuery } = useLumpsumPlansContext()
  const headerAppearance = resolveRebalanceHeaderAppearance(rebalancePlansData?.riskProfile)
  const isHeaderActive = planMode === PlansPlanMode.REBALANCE
  const isHeaderLoading = isListPlansHeaderLoading(
    PlansPlanMode.REBALANCE,
    rebalancePlansData != null,
    rebalancePlansQuery
  )
  const isPlansEmpty = !isHeaderLoading && isPlansListFullyEmpty(rebalancePlansData?.categories)
  const isPlansFetchFailed = isListPlansFetchFailed(PlansPlanMode.REBALANCE, rebalancePlansQuery)
  const isSubscriptionEligible = rebalancePlansData?.isPlanSubscriptionEligible === true
  // Locked empty / top-shape still needs this header; unlocked empty keeps the list empty state only.
  const shouldHideForEmptyList = isPlansEmpty && isSubscriptionEligible
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const plansListHero = useMemo(
    () => getRebalancePlansListHeroFields(rebalancePlansData),
    [rebalancePlansData]
  )

  if (shouldHideForEmptyList || isPlansFetchFailed) {
    return null
  }

  function handleMissedGainsInfoClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumRebalance,
        eventId: PlansLumpsumListEventIds.MissedGainsInfo,
        buttonLabel: 'Missed gains info'
      }
    })
    openBottomSheet(
      <MissedGainsInfoBottomSheet
        onDismiss={closeBottomSheet}
        rebalancePlansData={rebalancePlansData}
      />
    )
  }

  function handleRebalancePlanInfoClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumRebalance,
        eventId: PlansLumpsumListEventIds.PlanInfo,
        buttonLabel: 'Plan info'
      }
    })
    openBottomSheet(<RebalancePlanInfoBottomSheet onDismiss={closeBottomSheet} />)
  }

  function handleSeeWhatChangedClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumRebalance,
        eventId: PlansLumpsumListEventIds.SeeWhatChanged,
        buttonLabel: 'See what changed'
      }
    })
    openBottomSheet(
      <RebalancePlanUpdatedBottomSheet
        planSnapshotDate={rebalancePlansData?.planSnapshotDate ?? 0}
        planDurationInMonths={rebalancePlansData?.planDurationInMonths}
        newRecommendationsCount={rebalancePlansData?.newRecommendationsCount}
        updatedRecommendationsCount={rebalancePlansData?.updatedRecommendationsCount}
        invalidatedRecommendationsCount={rebalancePlansData?.invalidatedRecommendationsCount}
        onDismiss={closeBottomSheet}
      />
    )
  }

  const connectedSummaryLines = isHeaderLoading
    ? []
    : getRebalanceConnectedSummaryLines(plansListHero, handleMissedGainsInfoClick)
  const showPlanUpdatedBanner =
    !isHeaderLoading &&
    shouldShowPlanUpdatedBanner(rebalancePlansData?.planCount, rebalancePlansData?.planSnapshotDate)

  return (
    <div>
      {isHeaderLoading ? (
        <HeaderHeroSkeleton />
      ) : (
        <>
          {showPlanUpdatedBanner ? (
            <ListPlansPlanUpdatedBanner
              bannerType={ListPlansPlanUpdatedBannerType.REBALANCE}
              planSnapshotDate={rebalancePlansData?.planSnapshotDate ?? 0}
              planDurationInMonths={rebalancePlansData?.planDurationInMonths}
              onSeeWhatChangedClick={handleSeeWhatChangedClick}
            />
          ) : null}
          <div
            className={`relative overflow-visible px-5${plansListHero.isTopShape && !isSubscriptionEligible ? ' pb-8' : ' pb-1'}${showPlanUpdatedBanner ? '' : ' pt-6'}`}
          >
            <HeaderRiveBackdrop
              isActive={isHeaderActive}
              riveSrc={headerAppearance.riveSrc}
              headerSvgSrc={headerAppearance.headerSvgSrc}
              backgroundColor={headerAppearance.backgroundColor}
            />

            <div className="relative z-[1] flex gap-3">
              <div className="min-w-0 flex-1 max-w-[calc(100%-130px)]">
                {plansListHero.planPeriodLabel.length > 0 ? (
                  <div className={headerAppearance.reviewedOnClassName}>{plansListHero.planPeriodLabel}</div>
                ) : null}

                <div className="pt-1 whitespace-pre-line text-[22px] font-[500] leading-[130%] text-[#232323]">
                  {plansListHero.headline}
                </div>

                {connectedSummaryLines.length > 0 ? (
                  <div className="pt-3 pl-[2px]">
                    <ConnectedSummaryLinesV2
                      lines={connectedSummaryLines}
                      connectorColor={headerAppearance.connectorColor}
                      connectorThickness={2}
                      gap={4}
                    />
                  </div>
                ) : null}

                {plansListHero.isTopShape ? (
                  <p className="pt-3 text-[13px] font-[400] leading-[150%] text-[#3D3D3D]">
                    {REBALANCE_TOP_SHAPE_STATUS_COPY}
                  </p>
                ) : null}
              </div>
            </div>

            <HeaderHeroDescription
              description={REBALANCE_PLAN_DESCRIPTION}
              onLearnMoreClick={handleRebalancePlanInfoClick}
              className="relative z-[1] w-full max-w-[300px]"
            />
          </div>
        </>
      )}
    </div>
  )
}

type RebalancePlansListHeroFields = {
  planPeriodLabel: string
  headline: string
  isTopShape: boolean
  sellFundCount: number
  buyFundCount: number
  underReviewFundCount: number
  missedGains: number
}

function getRebalancePlansListHeroFields(
  rebalancePlansData: LumpsumRebalancePlansResponse | undefined
): RebalancePlansListHeroFields {
  const actionableFundCount = rebalancePlansData?.actionableFundCount ?? 0
  const isTopShape = actionableFundCount === 0

  return {
    planPeriodLabel: formatRebalancePlansPeriodLabel(
      rebalancePlansData?.planSnapshotDate,
      rebalancePlansData?.planDurationInMonths
    ),
    headline: isTopShape ? 'Your portfolio is in top shape' : 'Rebalance Plan',
    isTopShape,
    sellFundCount: rebalancePlansData?.sellFundCount ?? 0,
    buyFundCount: rebalancePlansData?.buyFundCount ?? 0,
    underReviewFundCount: rebalancePlansData?.underReviewFundCount ?? 0,
    missedGains: rebalancePlansData?.missedGains ?? 0
  }
}

function getRebalanceConnectedSummaryLines(
  plansListHero: RebalancePlansListHeroFields,
  onMissedGainsInfoClick: () => void
): ConnectedSummaryLine[] {
  const lines: ConnectedSummaryLine[] = []

  if (plansListHero.isTopShape) {
    if (plansListHero.underReviewFundCount > 0) {
      lines.push({
        html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.underReviewFundCount)}</span> in review`
      })
    }
    return lines
  }

  if (plansListHero.sellFundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.sellFundCount)}</span> to sell`
    })
  }

  if (plansListHero.buyFundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.buyFundCount)}</span> to buy`
    })
  }

  if (plansListHero.underReviewFundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.underReviewFundCount)}</span> under review`
    })
  }

  if (plansListHero.missedGains > 0) {
    const formattedMissedGains = formatRupeeLabel({ rawAmount: plansListHero.missedGains })
    lines.push({
      content: (
        <>
          <span className="font-[500] text-[#3d3d3d]">{formattedMissedGains}</span> missed{' '}
          <HeaderInfoAfterWord word="gains" />
        </>
      ),
      onClick: onMissedGainsInfoClick
    })
  }

  return lines
}

function formatFundCountLabel(fundCount: number): string {
  return `${fundCount} fund${fundCount === 1 ? '' : 's'}`
}

function formatRebalancePlansPeriodLabel(
  planSnapshotDate?: number | null,
  planDurationInMonths?: number | null
): string {
  if (!planSnapshotDate) {
    return ''
  }

  const planMonthRangeLabel = formatPlanDateRange(planSnapshotDate, planDurationInMonths ?? 1)
    .replace(/ plan$/, '')
    .replace(' - ', '-')

  if (planMonthRangeLabel.length === 0) {
    return ''
  }

  return `Plan for ${planMonthRangeLabel}`
}

const REBALANCE_FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.REBALANCE)
const REBALANCE_HEADER_STYLE = {
  backgroundColor: REBALANCE_FLOW_THEME.topColor,
  chromeBackground: REBALANCE_FLOW_THEME.chromeBackground,
  reviewedOnClassName: REBALANCE_FLOW_THEME.periodLabelClassName,
  connectorColor: REBALANCE_FLOW_THEME.connectorColor
}

export function resolveRebalanceHeaderAppearance(
  riskProfile?: AllocationRiskProfile | null
): LumpsumHeaderAppearance {
  return resolveRebalanceHeaderAssetAppearance({
    riskProfile,
    ...REBALANCE_HEADER_STYLE
  })
}
